import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { Download, Eye, RotateCcw } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { usePageActions } from "@/shared/hooks/usePageActions";
import { sortByColumn, useTableSort } from "@/shared/hooks/useTableSort";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { TableToolbar } from "../components/TableToolbar";
import { SortableTableHead } from "../components/SortableTableHead";
import { inputClass, selectClass } from "../components/FormField";
import {
  enterpriseInvoiceStatusFilterOptions,
} from "../data";
import {
  useHospitalInvoices,
  type HospitalInvoiceRow,
} from "../hooks/useHospitalInvoices";
import { InvoiceNatureBadge, invoiceNatureShortLabel } from "../components/InvoiceNatureBadge";
import { cn } from "@/shared/lib/cn";
import type { InvoiceDisplayStatus } from "../types";

const hospitalStatusBadgeClass: Record<
  Extract<InvoiceDisplayStatus, "submitted" | "inReview" | "approved">,
  string
> = {
  submitted: "bg-teal-soft text-teal",
  inReview: "bg-yellow-soft text-yellow",
  approved: "bg-green/15 text-green",
};

function getHospitalDisplayStatus(
  invoice: HospitalInvoiceRow,
): Extract<InvoiceDisplayStatus, "submitted" | "inReview" | "approved"> {
  return invoice.displayStatus;
}

export function HospitalViewInvoicesPage() {
  const { t } = useTranslation();
  const labels = t.safecheck.invoices.hospitalView;
  const { exportTable } = usePageActions();
  const { invoices, loading, error, usingApi } = useHospitalInvoices();

  const [topSearch, setTopSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [tableSearch, setTableSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);
  const { sortKey, sortDirection, toggleSort } = useTableSort("displayId", "desc");

  const handleSort = (key: string) => {
    toggleSort(key);
    setPage(1);
  };

  const resetFilters = () => {
    setTopSearch("");
    setStatusFilter("");
    setTableSearch("");
    setPage(1);
  };

  const filtered = useMemo(() => {
    return invoices.filter((invoice) => {
      const haystack =
        `${invoice.displayId} ${invoice.company} ${invoice.reference} ${invoice.fileName}`.toLowerCase();
      const displayStatus = getHospitalDisplayStatus(invoice);

      const byTopSearch = topSearch ? haystack.includes(topSearch.toLowerCase()) : true;
      const byTableSearch = tableSearch ? haystack.includes(tableSearch.toLowerCase()) : true;
      const byStatus = statusFilter ? displayStatus === statusFilter : true;

      return byTopSearch && byTableSearch && byStatus;
    });
  }, [invoices, topSearch, statusFilter, tableSearch]);

  const sorted = useMemo(
    () =>
      sortByColumn(filtered, sortKey, sortDirection, (invoice, key) => {
        if (key === "displayId") return invoice.displayId;
        if (key === "company") return invoice.company;
        if (key === "invoice") return invoice.fileName;
        if (key === "status") return getHospitalDisplayStatus(invoice);
        return "";
      }),
    [filtered, sortKey, sortDirection],
  );

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const start = sorted.length === 0 ? 0 : (currentPage - 1) * pageSize;
  const pageData = sorted.slice(start, start + pageSize);

  const columnDefs = [
    { key: "displayId", label: labels.columns.id, sortable: true },
    { key: "company", label: labels.columns.company, sortable: true },
    { key: "nature", label: labels.columns.nature, sortable: true },
    { key: "invoice", label: labels.columns.invoice, sortable: true },
    { key: "status", label: labels.columns.status, sortable: true },
    { key: "actions", label: labels.columns.actions, sortable: false },
  ] as const;

  const handleExportCsv = () => {
    exportTable(
      "factures-prestataire.csv",
      [
        labels.columns.id,
        labels.columns.company,
        labels.columns.nature,
        labels.columns.invoice,
        labels.columns.status,
      ],
      filtered.map((invoice) => {
        const displayStatus = getHospitalDisplayStatus(invoice);
        return [
          String(invoice.displayId),
          invoice.company,
          invoiceNatureShortLabel(
            invoice.invoiceNature === "forfait_mensuel" ? "forfait_mensuel" : "hors_forfait",
          ),
          invoice.fileName,
          labels.statuses[displayStatus],
        ];
      }),
    );
  };

  return (
    <>
      <Breadcrumbs
        homeHref="/dashboard/hospital"
        items={[{ label: t.safecheck.nav.viewInvoices }]}
      />

      <div className="theme-card">
        {usingApi && !loading && (
          <p className="border-b border-border px-5 py-2 text-xs text-muted">
            {invoices.length} facture{invoices.length > 1 ? "s" : ""}
          </p>
        )}
        {loading && (
          <p className="border-b border-border px-5 py-2 text-xs text-muted">Chargement…</p>
        )}
        {error && <p className="border-b border-border px-5 py-2 text-xs text-danger">{error}</p>}

        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              value={topSearch}
              onChange={(event) => {
                setTopSearch(event.target.value);
                setPage(1);
              }}
              placeholder={labels.searchPlaceholder}
              className={`${inputClass} w-full min-w-[200px] max-w-xs`}
            />
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className={`${selectClass} w-full min-w-[180px] max-w-xs`}
            >
              {enterpriseInvoiceStatusFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.value
                    ? labels.statuses[option.value as keyof typeof labels.statuses]
                    : labels.allStatuses}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportCsv}
              className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-card-muted"
            >
              <Download size={15} />
              {labels.exportCsv}
            </button>
            <button
              type="button"
              onClick={resetFilters}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-card-muted"
              aria-label={t.safecheck.reset}
            >
              <RotateCcw size={15} />
            </button>
          </div>
        </div>

        <TableToolbar
          search={tableSearch}
          onSearchChange={(value) => {
            setTableSearch(value);
            setPage(1);
          }}
          searchPlaceholder={t.safecheck.search}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          showLabel={t.safecheck.show}
          entriesLabel={t.safecheck.entries}
        />

        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-border bg-card-muted/50">
                {columnDefs.map((column) => (
                  <SortableTableHead
                    key={column.key}
                    label={column.label}
                    columnKey={column.key}
                    activeKey={sortKey}
                    direction={sortDirection}
                    onSort={handleSort}
                    sortable={column.sortable}
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {pageData.map((invoice) => {
                const displayStatus = getHospitalDisplayStatus(invoice);
                return (
                  <tr
                    key={invoice.id}
                    className="border-b border-border last:border-0 hover:bg-card-muted/30"
                  >
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      <Link
                        to={`/dashboard/invoices/${invoice.displayId}`}
                        className="text-primary transition-colors hover:text-primary/80"
                      >
                        {invoice.displayId}
                      </Link>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground">{invoice.company}</td>
                    <td className="px-4 py-3">
                      <InvoiceNatureBadge
                        nature={invoice.invoiceNature}
                        isForfaitMensuel={invoice.isForfaitMensuel}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        disabled
                        className="cursor-not-allowed text-sm font-medium text-primary/40"
                      >
                        {labels.download}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
                          hospitalStatusBadgeClass[displayStatus],
                        )}
                      >
                        {labels.statuses[displayStatus]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/dashboard/invoices/${invoice.displayId}`}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-card-muted"
                      >
                        <Eye size={14} />
                        {labels.actions.view}
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {pageData.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-10 text-center text-sm text-muted">
                    {labels.empty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-5 py-4 text-sm text-muted">
          <span>
            {labels.pagination.showing}{" "}
            {sorted.length === 0 ? 0 : start + 1} {labels.pagination.to}{" "}
            {Math.min(start + pageSize, sorted.length)} {labels.pagination.of}{" "}
            {sorted.length} {labels.pagination.entries}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={currentPage === 1}
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.previous}
            </button>
            {Array.from({ length: totalPages }, (_, index) => index + 1).map((pageNum) => (
              <button
                key={pageNum}
                type="button"
                onClick={() => setPage(pageNum)}
                className={cn(
                  "min-w-9 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                  pageNum === currentPage
                    ? "bg-primary text-white"
                    : "border border-border text-muted-foreground hover:bg-card-muted",
                )}
              >
                {pageNum}
              </button>
            ))}
            <button
              type="button"
              disabled={currentPage >= totalPages}
              onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
              className="rounded-lg border border-border px-3 py-1.5 text-muted-foreground transition-colors hover:bg-card-muted disabled:cursor-not-allowed disabled:opacity-40"
            >
              {labels.pagination.next}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
